import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type ComponentProps,
} from "react";
import {
  BfButton,
  BfInput,
  BfTextarea,
  BfModal,
  BfSkeletonCard,
} from "@bitsandflames/web-components/core";
import { DialogTitle } from "@headlessui/react";
import { AlertCircle, CheckCircle2, X, Type, ArrowUpRight } from "./PixelIcons";
export function Button(props: ComponentProps<typeof BfButton>) {
  return <BfButton icon={false} {...props} />;
}
export function Field(props: ComponentProps<typeof BfInput>) {
  return <BfInput icon={Type} helper="" {...props} />;
}
export function Textarea(props: ComponentProps<typeof BfTextarea>) {
  return <BfTextarea helper="" {...props} />;
}
export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}
export function Spinner({
  label = "Loading your workspace…",
  variant = "list",
}: {
  label?: string;
  variant?: "card" | "list" | "metadata" | "document";
}) {
  return (
    <div className="loading">
      <BfSkeletonCard variant={variant} label={label} rows={3} />
    </div>
  );
}
export function ErrorBox({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  return (
    <div className="error-box" role="alert">
      <AlertCircle size={18} />
      <div>{message}</div>
      {retry && <button onClick={retry}>Try again</button>}
    </div>
  );
}
export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <ArrowUpRight size={24} />
      </div>
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <BfModal
      open={open}
      onClose={onClose}
      size={wide ? "2xl" : "lg"}
      panelClassName="iron-modal"
      mobileFullscreen
    >
      <div className="modal-heading">
        <div>
          <DialogTitle className="modal-title">{title}</DialogTitle>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button
          className="icon-button"
          aria-label="Close dialog"
          onClick={onClose}
        >
          <X size={19} />
        </button>
      </div>
      {children}
    </BfModal>
  );
}
export function Select({
  label,
  value,
  onChange,
  options,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <label className="select-field">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
const ToastContext = createContext<(message: string, error?: boolean) => void>(
  () => {},
);
export const useToast = () => useContext(ToastContext);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ message: string; error: boolean }>();
  return (
    <ToastContext.Provider
      value={(message, error = false) => {
        setToast({ message, error });
        setTimeout(
          () =>
            setToast((current) =>
              current?.message === message ? undefined : current,
            ),
          5000,
        );
      }}
    >
      {children}
      {toast && (
        <div
          className={`toast ${toast.error ? "toast-error" : ""}`}
          role={toast.error ? "alert" : "status"}
        >
          {toast.error ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
          <button
            aria-label="Dismiss notification"
            onClick={() => setToast(undefined)}
          >
            <X size={15} />
          </button>
        </div>
      )}
    </ToastContext.Provider>
  );
}
